import type { ProcedureExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type {
  WorksheetFieldDef,
  WorksheetOption,
} from "../worksheet-field-defs";

export type ProcedureShowWhen = { field: string; in: readonly string[] };

export type ProcedureFieldDef = WorksheetFieldDef<ProcedureShowWhen>;

export type ProcedureDefinition = {
  examType: ProcedureExamType;
  version: number;
  allNormal: boolean;
  indications: readonly WorksheetOption[];
  views: readonly WorksheetOption[];
  fields: readonly ProcedureFieldDef[];
  siteFieldId: string;
  needleTipFieldId: string;
  completeWhenAnswered: readonly string[];
  codesBySite: Readonly<Record<string, readonly string[]>>;
  codesFromField?: {
    whenSite: string;
    fieldId: string;
    codes: Readonly<Record<string, readonly string[]>>;
  };
};

export const single = (
  id: string,
  label: string,
  options: readonly WorksheetOption[],
  showWhen?: ProcedureShowWhen,
  normal: string | null = null,
): ProcedureFieldDef => ({
  kind: "single",
  id,
  label,
  options,
  normal,
  showWhen,
});

export const routine = (
  id: string,
  label: string,
  options: readonly WorksheetOption[],
  normal: string,
): ProcedureFieldDef => single(id, label, options, undefined, normal);

export const multi = (
  id: string,
  label: string,
  options: readonly WorksheetOption[],
  showWhen?: ProcedureShowWhen,
): ProcedureFieldDef => ({ kind: "multi", id, label, options, showWhen });

export const number = (
  id: string,
  label: string,
  unit: string,
  extra: {
    integer?: boolean;
    min?: number;
    max?: number;
    showWhen?: ProcedureShowWhen;
  } = {},
): ProcedureFieldDef => ({ kind: "number", id, label, unit, ...extra });

export const text = (
  id: string,
  label: string,
  showWhen?: ProcedureShowWhen,
): ProcedureFieldDef => ({ kind: "text", id, label, showWhen });

export const option = (
  value: string,
  label: string,
  revealsText?: boolean,
): WorksheetOption =>
  revealsText ? { value, label, revealsText } : { value, label };

export const OTHER_TEXT = option("other", "Other", true);

export const YES_NO = [option("yes", "Yes"), option("no", "No")];

export const YES_PARTIAL_NO = [
  option("yes", "Yes"),
  option("partial", "Partial"),
  option("no", "No"),
];

export const PRESENT_ABSENT_NOT_ASSESSED = [
  option("present", "Present"),
  option("absent", "Absent"),
  option("not_assessed", "Not assessed"),
];

export const APPROACH = single("approach", "Approach", [
  option("in_plane", "In-plane"),
  option("out_of_plane", "Out-of-plane"),
]);

export const NEEDLE_GAUGE = number("needle_gauge", "Needle gauge", "G", {
  integer: true,
  max: 34,
});

export const SHARED_FIELDS: readonly ProcedureFieldDef[] = [
  single("side", "Side", [
    option("left", "Left"),
    option("right", "Right"),
    option("bilateral", "Bilateral"),
  ]),
  single("consent", "Consent", [
    option("verbal", "Verbal"),
    option("written", "Written"),
  ]),
  routine(
    "time_out_completed",
    "Safety check / time-out completed",
    YES_NO,
    "yes",
  ),
  routine("aseptic_technique", "Aseptic technique", YES_NO, "yes"),
  routine(
    "post_procedure_advice_given",
    "Post-procedure advice given",
    YES_NO,
    "yes",
  ),
  single(
    "needle_in_target_image_saved",
    "Needle-in-target image saved",
    YES_NO,
  ),
];

export const SHARED_FIELD_IDS = SHARED_FIELDS.map((field) => field.id);
